import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../context/AuthContext";
import { ImagePlus, X } from "lucide-react";

const MAX_PHOTOS = 3;
const inputCls = "w-full bg-transparent border rounded-md px-3 py-2 text-sm outline-none focus:border-[var(--gf-accent)]";
const inputStyle = { borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" };

/** Returns an error message for the first unanswered required question, else null. */
export function validateAnswers(questions = [], answers = {}) {
  for (const q of questions) {
    if (!q.required) continue;
    const a = answers[q.id] || {};
    if (q.type === "photo") {
      if (!(a.photo_ids || []).length) return `"${q.label}" needs at least one photo.`;
    } else if (!String(a.value || "").trim()) {
      return `"${q.label}" is required.`;
    }
  }
  return null;
}

/** Shapes the local answer map into the API payload. */
export function answersPayload(questions = [], answers = {}) {
  return questions.map((q) => ({
    question_id: q.id,
    value: q.type === "photo" ? null : (answers[q.id]?.value ?? null),
    photo_ids: q.type === "photo" ? (answers[q.id]?.photo_ids || []) : [],
  }));
}

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

/** Customer-facing answer form for a service's approved booking questions. */
export const BookingQuestionsForm = ({ questions = [], answers = {}, onChange, authHeader, idPrefix = "bq" }) => {
  const [busy, setBusy] = useState(false);
  if (!questions.length) return null;

  const set = (qid, patch) => onChange({ ...answers, [qid]: { ...(answers[qid] || {}), ...patch } });

  const addPhoto = async (q, file) => {
    if (!file) return;
    const current = answers[q.id]?.photo_ids || [];
    if (current.length >= MAX_PHOTOS) { toast.error(`Up to ${MAX_PHOTOS} photos.`); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("That image is over 5MB — please pick a smaller one."); return; }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Only JPEG, PNG and WebP images are supported."); return;
    }
    setBusy(true);
    try {
      const data_url = await fileToDataUrl(file);
      const r = await axios.post(`${API}/uploads/booking-photo`,
        { content_type: file.type, filename: file.name, data_url },
        { headers: authHeader() });
      set(q.id, { photo_ids: [...current, r.data.id] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't upload that photo");
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = (q, id) =>
    set(q.id, { photo_ids: (answers[q.id]?.photo_ids || []).filter((p) => p !== id) });

  return (
    <div className="space-y-4" data-testid={`${idPrefix}-form`}>
      <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
        A few questions from this business
      </div>
      {questions.map((q) => {
        const a = answers[q.id] || {};
        const label = (
          <label className="text-xs" style={{ color: "var(--gf-text-soft)" }}>
            {q.label}{q.required ? <span style={{ color: "var(--gf-accent)" }}> *</span> : <span className="opacity-60"> (optional)</span>}
          </label>
        );
        return (
          <div key={q.id} data-testid={`${idPrefix}-q-${q.id}`}>
            {label}
            {q.help && <div className="text-[11px] mb-1" style={{ color: "var(--gf-text-muted)" }}>{q.help}</div>}
            {q.type === "long_text" ? (
              <textarea data-testid={`${idPrefix}-input-${q.id}`} rows={3} maxLength={1500} value={a.value || ""}
                        onChange={(e) => set(q.id, { value: e.target.value })} className={inputCls} style={inputStyle} />
            ) : q.type === "select" ? (
              <select data-testid={`${idPrefix}-input-${q.id}`} value={a.value || ""}
                      onChange={(e) => set(q.id, { value: e.target.value })} className={inputCls} style={inputStyle}>
                <option value="">Select…</option>
                {(q.choices || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : q.type === "yes_no" ? (
              <div className="flex gap-2 mt-1">
                {["Yes", "No"].map((v) => (
                  <button key={v} type="button" data-testid={`${idPrefix}-input-${q.id}-${v.toLowerCase()}`}
                          onClick={() => set(q.id, { value: v })}
                          className="rounded-full border px-4 py-1.5 text-xs"
                          style={{
                            borderColor: a.value === v ? "var(--gf-accent)" : "var(--gf-border-strong)",
                            background: a.value === v ? "var(--gf-accent)" : "transparent",
                            color: a.value === v ? "var(--gf-on-accent)" : "var(--gf-text-soft)",
                          }}>
                    {v}
                  </button>
                ))}
              </div>
            ) : q.type === "photo" ? (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {(a.photo_ids || []).map((pid) => (
                  <span key={pid} data-testid={`${idPrefix}-photo-${pid}`}
                        className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px]"
                        style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)" }}>
                    Photo attached
                    <button type="button" onClick={() => removePhoto(q, pid)} aria-label="Remove photo">
                      <X size={12} />
                    </button>
                  </span>
                ))}
                {(a.photo_ids || []).length < MAX_PHOTOS && (
                  <label className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] cursor-pointer"
                         style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
                    <ImagePlus size={13} /> {busy ? "Uploading…" : "Add photo"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                           data-testid={`${idPrefix}-photo-input-${q.id}`}
                           onChange={(e) => { addPhoto(q, e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                )}
              </div>
            ) : (
              <input data-testid={`${idPrefix}-input-${q.id}`}
                     type={q.type === "number" ? "number" : "text"} maxLength={500} value={a.value || ""}
                     onChange={(e) => set(q.id, { value: e.target.value })} className={inputCls} style={inputStyle} />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BookingQuestionsForm;
